/* -------------------------- Design imports start -------------------------- */
import { KeyboardArrowDown } from '@mui/icons-material'
import { Alert, Box, CircularProgress, Collapse, Divider, Grid, Link, Typography } from '@mui/material'
import React, { type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Form, FormField, type FormFieldType } from '../../Form'
import { getPropertyByString, setPropertyByString } from '../../Form/utils/functions'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import GetConstant from '../utils/constants'
import { type GuestRequest } from '../utils/types'
import Button from '../../../components/Button'
import { UnauthS3FileUpload } from '../../Filesystem'
import useMobileService from '../../../hooks/useMobileService'
import LogTool from '../../../logger/logTools'
import { missingRequiredFormFields } from '../../../utils/functions'
import { useCreateGuestRequest } from '../hooks/useGuestRequestMutations'
import AnimatedTaskDoneIcon from '../../../components/animatedIcons/taskDoneIcon';

/* ------------------------- Functional imports end ------------------------- */

type Props = {
  selectedForm: string
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function UnauthRequestForm(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'UnauthRequestForm', enable: true, logLevel: 'warn' })
  const { t } = useTranslation(['common', 'request'])
  const { selectedForm } = props
  const createGuestRequestMutation = useCreateGuestRequest()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [loading, setLoading] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showThankYouNote, setShowThankYou] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const [guestRequest, setGuestRequest] = useState<GuestRequest | undefined>(undefined)
  const [fileList, setFileList] = useState<any[]>([])
  const isMobile = useMobileService()
  /* ----------------------------- Data states end ---------------------------- */

  /* ---------------------------- Use Effects start --------------------------- */
  useEffect(() => {
    guestRequest && isMobile ? setExpanded(true) : setExpanded(false)
  }, [guestRequest, isMobile])
  /* ----------------------------- Use Effects end ---------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleSubmitGuestRequest = async (): Promise<void> => {
    if (!guestRequest) {
      log.error('Guest request is undefined')
      return
    }

    setLoading(true)
    try {
      await createGuestRequestMutation.mutateAsync({
        requestData: guestRequest,
        files: fileList,
      })

      // visual feedback
      setShowThankYou(true)
      toast.success(t('request:feedback.success.requestSent'))

      // reset guest request states
      setGuestRequest(prev => {
        if (!prev) return prev
        return {
          ...prev,
          quantity: '',
          message: undefined
        }
      })
      setFileList([])
      setLoading(false)
    } catch (error) {
      log.error('Error posting Guest Request -> ', error)
      toast.error(t('request:feedback.error.requestNotSent'))
      setLoading(false)
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const guestRequestFormFields: FormFieldType[] = GetConstant({
    name: 'guestRequestFormFields',
  }) as FormFieldType[]
  const mobileBaseFields = ['name', 'email']
  const mobileExpandedFields = [
    'company',
    'phone',
    'quantity',
    'material',
    'message',
  ]
  const mobileBaseFormFields: FormFieldType[] = guestRequestFormFields.filter(
    (field: FormFieldType) => mobileBaseFields.includes(field.key as string)
  )
  const mobileExpandedFormFields: FormFieldType[] = guestRequestFormFields.filter(
    (field: FormFieldType) => mobileExpandedFields.includes(field.key as string)
  )

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  if (loading)
    return (
      <Grid container justifyContent="center" alignItems="center">
        <CircularProgress />
      </Grid>
    )
  if (showThankYouNote)
    return (
      <Box
        id="thankYouGuestRequest"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        pt="120px"
      >
        <AnimatedTaskDoneIcon fontSize={'120px'} />
        <Typography variant="h5" fontWeight={600} mt="16px">
          {t('request:content.heading.thankYouRequest')}
        </Typography>
        <Typography variant="body1" mt="10px">
          {t('request:content.heading.weWillContactYouSoon')}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          onClick={() => setShowThankYou(false)}
          sx={{
            textTransform: 'none',
            color: 'whitesmoke',
            mt: '50px',
          }}
        >
          {t('request:interaction.button.startAnotherRequest')}
        </Button>
      </Box>
    )
  if (isMobile)
    return (
      <Grid container justifyContent="center" alignItems="center" margin="auto" spacing={0}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Grid container justifyContent="center" alignItems="center" spacing={0}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} height={'80%'}>
              <Typography variant="h5" align="center">
                {t('request:content.heading.startYourRequestHere')}{' '}
                <b>{t('request:content.heading.request')}</b> <br />
                {t('request:content.heading.weAreLookingForwardToYourProject')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ height: '100%' }}
              >
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Form
                    formFields={mobileBaseFormFields}
                    formObject={guestRequest as GuestRequest}
                    onChange={(value: any) => setGuestRequest(value as GuestRequest)}
                    editing={true}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Collapse in={expanded}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ height: '100%' }}
                    >
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Form
                          formFields={mobileExpandedFormFields}
                          formObject={guestRequest as GuestRequest}
                          onChange={(value: any) => setGuestRequest(value as GuestRequest)}
                          editing={true}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                          {t('request:content.label.fileUpload')} *
                        </Typography>
                        <UnauthS3FileUpload
                          fileList={fileList}
                          setFileList={setFileList}
                          selectedForm={selectedForm}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Typography
                          variant="caption"
                          align="center"
                          color={selectedForm === 'unauthRequestForm' ? '' : 'gray'}
                        >
                          {t('request:content.label.requiredFieldsText')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
                {showAlert && (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Alert severity="error" onClose={() => setShowAlert(false)}>
                      {t('request:feedback.error.requiredFields')}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Button
                    id="sendRequestButton"
                    variant="contained"
                    style={{
                      marginTop: '10px',
                      marginBottom: '10px',
                    }}
                    fullWidth
                    onClick={() => {
                      if (!expanded) {
                        setExpanded(true)
                        return
                      }
                      if (missingRequiredFormFields(guestRequestFormFields, guestRequest)) {
                        setShowAlert(true)
                        return
                      }
                      if (fileList.length === 0) {
                        setShowAlert(true)
                        toast.error(t('request:feedback.error.fileUploadRequired'))
                        return
                      }
                      if (guestRequest === undefined) return
                      void (async () => {
                        await handleSubmitGuestRequest()
                      })()
                    }}
                  >
                    {!expanded ? (
                      <KeyboardArrowDown />
                    ) : (
                      t('request:interaction.button.sendRequest')
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )

  return (
    <Grid container justifyContent="center" alignItems="center" margin="auto" spacing={2}>
      <Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
        <Grid container justifyContent="center" alignItems="center" spacing={0}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} height={'80%'}>
            <Typography
              style={{ marginBottom: '20px' }}
              variant="h5"
              align="center"
              color={selectedForm === 'unauthRequestForm' ? '' : 'gray'}
            >
              {t('request:content.heading.startYourRequestHere')}{' '}
              <b>{t('request:content.heading.request')}</b> <br />
              {t('request:content.heading.weAreLookingForwardToYourProject')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{ height: '100%' }}
            >
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container spacing={1.2} sx={{ opacity: selectedForm === 'unauthRequestForm' ? 1 : 0.8 }}>
                  {/* Contact Information Group */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
                      {t('request:content.label.contactInformation')}
                    </Typography>
                  </Grid>
                  {guestRequestFormFields
                    .filter(field => ['name', 'email', 'company', 'phone'].includes(field.key as string))
                    .map(({ xs = 12, sm = 6, md = 6, lg = 6, xl = 6, ...field }) => (
                      <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} key={field.key}>
                        <FormField
                          field={field as any}
                          value={getPropertyByString(guestRequest, field.key as string)}
                          onChange={(value: any) => {
                            const updated = setPropertyByString(guestRequest, field.key as string, value)
                            setGuestRequest(updated as GuestRequest)
                          }}
                          disabled={false}
                          required={field.required}
                        />
                      </Grid>
                    ))}

                  {/* Request Details Group */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('request:content.label.requestDetails')}
                    </Typography>
                  </Grid>
                  {guestRequestFormFields
                    .filter(field => ['quantity', 'material', 'message'].includes(field.key as string))
                    .map(({ xs = 12, sm = 6, md = 6, lg = 6, xl = 6, ...field }) => (
                      <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} key={field.key}>
                        <FormField
                          field={field as any}
                          value={getPropertyByString(guestRequest, field.key as string)}
                          onChange={(value: any) => {
                            const updated = setPropertyByString(guestRequest, field.key as string, value)
                            setGuestRequest(updated as GuestRequest)
                          }}
                          disabled={false}
                          required={field.required}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ mt: '-12px' }}>
                <Typography
                  variant="caption"
                  align="center"
                  color={selectedForm === 'unauthRequestForm' ? '' : 'gray'}
                >
                  {t('request:content.label.requiredFieldsText')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                sx={{
                  opacity: selectedForm === 'unauthRequestForm' ? 1 : 0.8,
                }}
              >
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('request:content.label.fileUpload')} *
                </Typography>
                <UnauthS3FileUpload
                  fileList={fileList}
                  setFileList={setFileList}
                  selectedForm={selectedForm}
                />
              </Grid>
              {showAlert && (
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Alert severity="error" onClose={() => setShowAlert(false)}>
                    {t('request:feedback.error.requiredFields')}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Button
              id="sendRequestButton"
              variant="contained"
              style={{
                marginTop: '10px',
                marginBottom: '10px',
                opacity: selectedForm === 'unauthRequestForm' ? 1 : 0.6,
              }}
              fullWidth
              onClick={() => {
                if (missingRequiredFormFields(guestRequestFormFields, guestRequest)) {
                  setShowAlert(true)
                  return
                }
                if (fileList.length === 0) {
                  setShowAlert(true)
                  toast.error(t('request:feedback.error.fileUploadRequired'))
                  return
                }
                if (guestRequest === undefined) return
                void (async () => {
                  await handleSubmitGuestRequest()
                })()
              }}
            >
              {t('request:interaction.button.sendRequest')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography
              variant="caption"
              align="center"
              color={selectedForm === 'unauthRequestForm' ? '' : 'gray'}
            >
              {t('request:content.label.orSendEmailInstead')}{' '}
              <Link href="mailto:info@assemblean.com" underline="none">
                info@assemblean.com
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
